export type FrameExtractorCallback = (second: number, url: string) => void;

const SEEK_TIMEOUT_MS = 8_000;

type EnsureSecondsOptions = {
  replaceQueued?: boolean;
  retainQueuedSeconds?: ReadonlySet<number>;
};

export type VideoFrameExtractorOptions = {
  seekTimeoutMs?: number;
  fps?: number;
};

function createAbortError(): Error {
  const error = new Error('Video frame extraction aborted');
  error.name = 'AbortError';
  return error;
}

function waitForEvent(target: EventTarget, eventName: string, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    const onEvent = (): void => {
      cleanup();
      resolve();
    };
    const onError = (): void => {
      cleanup();
      reject(new Error(`Failed to load video (${eventName})`));
    };
    const onAbort = (): void => {
      cleanup();
      reject(createAbortError());
    };
    const cleanup = (): void => {
      target.removeEventListener(eventName, onEvent);
      target.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };
    target.addEventListener(eventName, onEvent);
    target.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function seekTo(
  video: HTMLVideoElement,
  time: number,
  signal?: AbortSignal,
  timeoutMs = SEEK_TIMEOUT_MS
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    const onSeeked = (): void => {
      cleanup();
      resolve();
    };
    const onError = (): void => {
      cleanup();
      reject(new Error('Video seek failed'));
    };
    const onAbort = (): void => {
      cleanup();
      reject(createAbortError());
    };
    const onTimeout = (): void => {
      cleanup();
      reject(new Error('Video seek timed out'));
    };
    const timeoutId = globalThis.setTimeout(onTimeout, timeoutMs);
    const cleanup = (): void => {
      globalThis.clearTimeout(timeoutId);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort, { once: true });
    video.currentTime = time;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to capture frame'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
}

export class VideoFrameExtractor {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly video: HTMLVideoElement;
  private readonly cleanupCallback: () => void;
  private readonly onFrame: FrameExtractorCallback;
  private readonly queue: number[] = [];
  private queued = new Set<number>();
  private done = new Set<number>();
  private waiters = new Map<number, Array<() => void>>();
  private processing = false;
  private disposed = false;
  private readonly metadataDuration: number;
  private readonly abortController = new AbortController();
  private readonly externalSignal: AbortSignal | undefined;

  private constructor(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    // Total number of frames to extract. At fps=1 this equals
    // Math.ceil(duration) (legacy behavior). At fps=4 this is
    // Math.ceil(duration * 4) — one frame per 0.25s cell. The bit array on the
    // backend has one extra trailing bit (`duration * fps + 1`) representing
    // the implicit end-of-video boundary; that bit is NOT extracted as a frame
    // because the video has no content past `duration`.
    private readonly frameCount: number,
    private readonly framesPerSecond: number,
    metadataDuration: number,
    onFrame: FrameExtractorCallback,
    cleanup: () => void,
    signal?: AbortSignal,
    private readonly seekTimeoutMs = SEEK_TIMEOUT_MS
  ) {
    this.video = video;
    this.canvas = canvas;
    this.ctx = ctx;
    this.onFrame = onFrame;
    this.cleanupCallback = cleanup;
    this.metadataDuration = metadataDuration;
    this.externalSignal = signal;
  }

  static create(
    sourceUrl: string,
    onFrame: FrameExtractorCallback,
    cleanup: () => void,
    signal?: AbortSignal,
    options?: VideoFrameExtractorOptions
  ): Promise<VideoFrameExtractor> {
    return (async () => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';
      video.muted = true;
      video.src = sourceUrl;
      await waitForEvent(video, 'loadedmetadata', signal);
      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error('Video duration unavailable');
      }
      await waitForEvent(video, 'loadeddata', signal);
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1;
      canvas.height = video.videoHeight || 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to acquire canvas context');
      }
      const effectiveFps = Math.max(1, Math.floor(options?.fps ?? 1));
      const frameCount = Math.max(0, Math.ceil(duration * effectiveFps));
      return new VideoFrameExtractor(
        video,
        canvas,
        ctx,
        frameCount,
        effectiveFps,
        duration,
        onFrame,
        cleanup,
        signal,
        options?.seekTimeoutMs
      );
    })();
  }

  /** Frame indices in [0, frameCount). At fps=1 this is the seconds count. */
  get totalSeconds(): number {
    return this.frameCount;
  }

  get bufferedSeconds(): number {
    let bufferedEnd = 0;
    try {
      for (let i = 0; i < this.video.buffered.length; i += 1) {
        bufferedEnd = Math.max(bufferedEnd, this.video.buffered.end(i));
      }
    } catch {
      return 0;
    }
    return Math.max(0, Math.min(this.frameCount, Math.floor(bufferedEnd * this.framesPerSecond)));
  }

  ensureSeconds(seconds: number[], priority?: boolean, options?: EnsureSecondsOptions): void {
    if (this.isDisposedOrAborted()) return;
    if (options?.replaceQueued) {
      this.clearQueuedSeconds(options.retainQueuedSeconds);
    }
    if (!seconds.length) return;
    const nextSeconds: number[] = [];
    const requested = new Set<number>();
    for (const second of seconds) {
      if (requested.has(second)) continue;
      requested.add(second);
      if (second < 0 || second >= this.frameCount) continue;
      if (this.done.has(second)) continue;
      if (this.queued.has(second)) {
        if (priority) nextSeconds.push(second);
        continue;
      }
      nextSeconds.push(second);
      this.queued.add(second);
    }
    if (!nextSeconds.length) return;
    if (priority) {
      this.removeQueuedSeconds(nextSeconds);
      for (const second of nextSeconds) {
        this.queued.add(second);
      }
      this.queue.unshift(...nextSeconds);
    } else {
      this.queue.push(...nextSeconds);
    }
    this.scheduleProcessing();
  }

  waitForSeconds(seconds: number[]): Promise<void> {
    if (this.isDisposedOrAborted()) return Promise.resolve();
    if (!seconds.length) return Promise.resolve();
    const tasks: Promise<void>[] = [];

    for (const second of seconds) {
      if (second < 0 || second >= this.frameCount) continue;
      if (this.done.has(second)) continue;
      const existing = this.waiters.get(second) ?? [];
      const promise = new Promise<void>((resolve) => {
        existing.push(resolve);
        this.waiters.set(second, existing);
      });
      tasks.push(promise);
    }

    return Promise.all(tasks).then(() => undefined);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abortController.abort();
    this.queue.length = 0;
    this.queued.clear();
    this.waiters.forEach((waiters) => {
      waiters.forEach((resolve) => {
        resolve();
      });
    });
    this.waiters.clear();
    try {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    } catch {
      // ignore
    }
    this.cleanupCallback();
  }

  private scheduleProcessing(): void {
    if (this.processing || this.isDisposedOrAborted()) return;
    this.processing = true;
    void this.processQueue().finally(() => {
      this.processing = false;
      if (!this.isDisposedOrAborted() && this.queue.length > 0) {
        this.scheduleProcessing();
      }
    });
  }

  private async processQueue(): Promise<void> {
    while (this.queue.length > 0 && !this.isDisposedOrAborted()) {
      const second = this.queue.shift()!;
      this.queued.delete(second);
      try {
        await this.captureFrame(second);
        this.done.add(second);
      } catch (err) {
        if (!this.isDisposedOrAborted()) {
          console.warn('Failed to capture frame', err);
        }
        this.done.add(second);
      } finally {
        const waiters = this.waiters.get(second);
        if (waiters) {
          waiters.forEach((resolve) => {
            resolve();
          });
          this.waiters.delete(second);
        }
      }
    }
  }

  private clearQueuedSeconds(retainSeconds?: ReadonlySet<number>): void {
    const droppedSeconds: number[] = [];
    if (retainSeconds) {
      const retainedSeconds: number[] = [];
      for (const second of this.queue) {
        if (retainSeconds.has(second)) {
          retainedSeconds.push(second);
        } else {
          droppedSeconds.push(second);
        }
      }
      this.queue.length = 0;
      this.queue.push(...retainedSeconds);
    } else {
      droppedSeconds.push(...this.queue.splice(0, this.queue.length));
    }
    for (const second of droppedSeconds) {
      this.queued.delete(second);
      const waiters = this.waiters.get(second);
      if (waiters) {
        waiters.forEach((resolve) => {
          resolve();
        });
        this.waiters.delete(second);
      }
    }
  }

  private removeQueuedSeconds(seconds: number[]): void {
    if (!seconds.length) return;
    const toRemove = new Set(seconds);
    let writeIndex = 0;
    for (const second of this.queue) {
      if (!toRemove.has(second)) {
        this.queue[writeIndex] = second;
        writeIndex += 1;
      }
    }
    this.queue.length = writeIndex;
  }

  private async captureFrame(second: number): Promise<void> {
    if (this.isDisposedOrAborted()) return;
    const timeSeconds = second / this.framesPerSecond;
    const clamped = Math.min(timeSeconds, Math.max(0, this.metadataDuration - 0.001));
    await seekTo(this.video, clamped, this.abortController.signal, this.seekTimeoutMs);
    if (this.isDisposedOrAborted()) return;
    this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
    const blob = await canvasToBlob(this.canvas);
    if (this.isDisposedOrAborted()) return;
    const url = URL.createObjectURL(blob);
    if (this.isDisposedOrAborted()) {
      URL.revokeObjectURL(url);
      return;
    }
    this.onFrame(second, url);
  }

  private isDisposedOrAborted(): boolean {
    return (
      this.disposed || this.abortController.signal.aborted || this.externalSignal?.aborted === true
    );
  }
}

let activeExtractor: VideoFrameExtractor | null = null;

export function registerFrameExtractor(extractor: VideoFrameExtractor | null): void {
  activeExtractor = extractor;
}

export function cleanupVideoPrefetcher(): void {
  if (activeExtractor) {
    activeExtractor.dispose();
    activeExtractor = null;
  }
}
